import { fetchAuthSession } from 'aws-amplify/auth';
import { userService } from './userService';

// Using centralized userService.getAuthToken() instead of duplicate code

// API Configuration
const PROD_API_BASE = 'https://5z9vmw0tg6.execute-api.eu-north-1.amazonaws.com/production';
const DEV_API_BASE = 'https://9vnb05of2j.execute-api.eu-north-1.amazonaws.com/development';

// Auto-select API by environment
const API_BASE_URL = (() => {
  const fromEnv = process.env.REACT_APP_API_URL;
  if (fromEnv && fromEnv.trim().length > 0) return fromEnv;
  if (typeof window !== 'undefined') {
    const host = window.location.host || '';
    const hostname = window.location.hostname || '';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isDevSite = host.includes('saas-application-dev.s3-website.eu-north-1.amazonaws.com');
    if (isLocalhost || isDevSite) return DEV_API_BASE;
  }
  return PROD_API_BASE;
})();

export interface Project {
  project_uuid: string;
  name: string;
  description?: string;
  color: string;
  manager_id?: string;
  has_workflow?: boolean;
  companion_data?: CompanionData;
  created_at: string;
  updated_at: string;
  knowledge_resources?: Array<{
    title: string;
    metadata?: any;
    created_at: string;
    plain_text: string;
    description: string;
    [key: string]: any;
  }>;
}

export interface CompanionData {
  id: string;
  name: string;
  avatar: string;
  role: string;
  description: string;
  responsibilities: string[];
  bestFor: string[];
  personality: string[];
  color: string;
  campaignType: 'sponsored_content' | 'affiliate' | 'ambassadorship' | 'product_seeding' | 'custom';
}

export interface CreateProjectRequest {
  name: string;
  description?: string;
  color?: string;
  manager_id?: string;
  companion_data?: CompanionData;
}

class ProjectsService {
  async getAllProjects(): Promise<Project[]> {
    try {
      const jwtToken = await userService.getAuthToken();
      
      const response = await fetch(`${API_BASE_URL}/projects`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.status}`);
      }

      const data = await response.json();
      console.log('[ProjectsService] Fetched projects:', data);
      
      // Return projects with consistent naming
      return data.data || [];
    } catch (error) {
      console.error('[ProjectsService] Error fetching projects:', error);
      throw error;
    }
  }

  async createProject(data: CreateProjectRequest): Promise<Project> {
    try {
      const jwtToken = await userService.getAuthToken();
      
      const response = await fetch(`${API_BASE_URL}/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          name: data.name,
          description: data.description,
          color: data.color,
          companion_data: data.companion_data
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create project: ${response.status}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('[ProjectsService] Error creating project:', error);
      throw error;
    }
  }

  async updateProject(projectId: string, data: UpdateProjectRequest): Promise<Project> {
    try {
      console.log('[ProjectsService] Updating project:', projectId);
      console.log('[ProjectsService] Update payload:', data);
      
      const jwtToken = await userService.getAuthToken();
      
      const response = await fetch(`${API_BASE_URL}/projects/${projectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify(data),
      });

      console.log('[ProjectsService] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ProjectsService] Error response:', errorText);
        throw new Error(`Failed to update project: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('[ProjectsService] Update response:', result);
      return result.data;
    } catch (error) {
      console.error('[ProjectsService] Error updating project:', error);
      throw error;
    }
  }

  async deleteProject(projectId: string): Promise<void> {
    try {
      const jwtToken = await userService.getAuthToken();
      
      const response = await fetch(`${API_BASE_URL}/projects/${projectId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete project: ${response.status}`);
      }

      console.log('[ProjectsService] Project deleted successfully');
    } catch (error) {
      console.error('[ProjectsService] Error deleting project:', error);
      throw error;
    }
  }

  async getProject(projectId: string): Promise<Project> {
    try {
      const jwtToken = await userService.getAuthToken();
      const response = await fetch(`${API_BASE_URL}/projects/${projectId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
      });
      if (!response.ok) throw new Error(`Failed to get project: ${response.status}`);
      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('[ProjectsService] Error getting project:', error);
      throw error;
    }
  }

  async removeKnowledgeResource(projectId: string, relationId: string): Promise<void> {
    try {
      const jwtToken = await userService.getAuthToken();
      const response = await fetch(`${API_BASE_URL}/projects/${projectId}/knowledge/${relationId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
      });
      if (!response.ok) throw new Error(`Failed to remove knowledge resource: ${response.status}`);
    } catch (error) {
      console.error('[ProjectsService] Error removing knowledge resource:', error);
      throw error;
    }
  }

  async removeKnowledgeResourceByResourceId(projectId: string, resourceId: string): Promise<void> {
    try {
      const jwtToken = await userService.getAuthToken();
      const response = await fetch(`${API_BASE_URL}/projects/${projectId}/knowledge/by-resource/${resourceId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
      });
      if (!response.ok) throw new Error(`Failed to remove knowledge resource by resource ID: ${response.status}`);
      console.log('[ProjectsService] Successfully removed knowledge resource by resource ID:', resourceId);
    } catch (error) {
      console.error('[ProjectsService] Error removing knowledge resource by resource ID:', error);
      throw error;
    }
  }
}

export const projectsService = new ProjectsService();

export interface AddKnowledgeResourceRequest {
  resource_name: string;
  resource_type: string;
  resource_path?: string;
  resource_id: string;
  metadata?: any;
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  color?: string;
  manager_id?: string;
  has_workflow?: boolean;
  companion_data?: CompanionData;
}

// Add knowledge resource association functionality
export const addKnowledgeResource = async (projectId: string, resourceId: string, resourceName: string): Promise<void> => {
  try {
    console.log('[ProjectsService] Adding knowledge resource:', { projectId, resourceId, resourceName });
    const jwtToken = await userService.getAuthToken();
    
    const requestPayload = {
      resource_name: resourceName,
      resource_type: 'document',
      resource_id: resourceId,
      metadata: {}
    };
    
    console.log('[ProjectsService] Request payload:', requestPayload);
    
    const response = await fetch(`${API_BASE_URL}/projects/${projectId}/knowledge`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwtToken}`,
      },
      body: JSON.stringify(requestPayload),
    });

    console.log('[ProjectsService] Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[ProjectsService] Error response:', errorText);
      throw new Error(`Failed to add knowledge resource: ${response.status} - ${errorText}`);
    }

    const responseData = await response.json();
    console.log('[ProjectsService] Knowledge resource added successfully:', responseData);
  } catch (error) {
    console.error('[ProjectsService] Error adding knowledge resource:', error);
    throw error;
  }
};
